import {FirebaseApp, getApps, initializeApp} from 'firebase/app'
import {
    Analytics,
    AnalyticsCallOptions,
    getAnalytics,
    initializeAnalytics,
    isSupported,
    logEvent,
    setDefaultEventParameters,
} from 'firebase/analytics'

export interface FireBaseConfig {
    enabled: boolean
    debugMode?: boolean
    apiKey: string
    authDomain: string
    projectId: string
    storageBucket: string
    messagingSenderId: string
    appId: string
    measurementId: string
}

export class Firebase {
    private static _instance: Firebase
    public analytics?: Analytics
    public app?: FirebaseApp
    private debugEnabled = false

    private constructor(firebaseConfig: FireBaseConfig) {
        if (!firebaseConfig?.enabled) {
            return
        }

        this.debugEnabled = !!firebaseConfig.debugMode

        if (getApps()?.length === 0) {
            this.app = initializeApp({
                apiKey: firebaseConfig.apiKey,
                authDomain: firebaseConfig.authDomain,
                projectId: firebaseConfig.projectId,
                storageBucket: firebaseConfig.storageBucket,
                messagingSenderId: firebaseConfig.messagingSenderId,
                appId: firebaseConfig.appId,
                measurementId: firebaseConfig.measurementId,
            })
        } else {
            this.app = getApps()[0]
        }

        void this.initAnalytics()
    }
    private initAnalytics() {
        try {
            if (this.debugEnabled) {
                this.analytics = initializeAnalytics(this.app!, {config: {debug_mode: true}})
                console.info('[Firebase] Analytics debug_mode enabled')
                this.scheduleDebugPing()
            } else {
                this.analytics = getAnalytics(this.app!)
            }

            console.info('[Firebase] Analytics initialized')
        } catch (error) {
            console.warn(
                '[Firebase] Analytics initialization failed or is not supported on this platform:',
                error
            )
        }
    }

    private scheduleDebugPing() {
        const send = (attempt: number) => {
            if (!this.analytics) {
                return
            }
            try {
                logEvent(this.analytics, 'debug_ping', {attempt})
                console.info(`[Firebase] debug_ping sent (attempt ${attempt})`)
            } catch (error) {
                console.warn(`[Firebase] debug_ping failed (attempt ${attempt}):`, error)
            }
        }

        send(1)
        setTimeout(() => send(2), 1000)
        setTimeout(() => send(3), 3000)
    }

    public static getInstance(firebaseConfig: FireBaseConfig) {
        if (!Firebase._instance) {
            Firebase._instance = new Firebase(firebaseConfig)
        }
        return Firebase._instance
    }

    public emit(
        eventName: string,
        eventParams?: {[key: string]: any} | undefined,
        options?: AnalyticsCallOptions | undefined
    ) {
        if (!this.analytics) {
            return
        }
        try {
            logEvent(this.analytics, eventName, eventParams, options)
        } catch (error) {
            console.warn(`[Firebase] emit failed (${eventName}):`, error)
        }
    }

    public _setDefaultEventParameters(args: any) {
        return setDefaultEventParameters(args)
    }
}
